import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { usePageMeta } from '../lib/usePageMeta';

interface SideBySideProps {
  eyebrow: string;
  title: string;
  body: string[];
  facts?: { label: string; value: string }[];
  photo: {
    src1600: string;
    src800: string;
    alt: string;
  };
  bg: 'white' | 'surface';
  photoSide: 'left' | 'right';
}

function SideBySide({
  eyebrow,
  title,
  body,
  facts,
  photo,
  bg,
  photoSide,
}: SideBySideProps): JSX.Element {
  const bgClass = bg === 'white' ? 'bg-brand-white' : 'bg-brand-surface';
  const eyebrowClass = 'text-brand-yellow-deep';
  return (
    <section className={`section ${bgClass}`}>
      <Container>
        <div
          className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
            photoSide === 'left' ? 'lg:[&>*:first-child]:order-2' : ''
          }`}
        >
          <div>
            <p className={`mb-3 text-xs font-semibold uppercase tracking-[0.22em] ${eyebrowClass}`}>
              {eyebrow}
            </p>
            <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
              {title}
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-brand-body-dark">
              {body.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>
            {facts ? (
              <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-brand-surface pt-6">
                {facts.map((fact) => (
                  <div key={fact.label}>
                    <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                      {fact.label}
                    </dt>
                    <dd className="mt-1 text-base font-semibold text-brand-charcoal">
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
          <div>
            <picture>
              <source
                type="image/webp"
                srcSet={`${photo.src800} 800w, ${photo.src1600} 1600w`}
                sizes="(min-width: 1024px) 560px, 100vw"
              />
              <img
                src={photo.src1600}
                alt={photo.alt}
                loading="lazy"
                className="aspect-[16/10] w-full rounded-lg object-cover shadow-card"
              />
            </picture>
          </div>
        </div>
      </Container>
    </section>
  );
}

const BUILDING_LAT = 28.3548298;
const BUILDING_LON = -80.6116973;
const MAP_BBOX = `${BUILDING_LON - 0.005}%2C${BUILDING_LAT - 0.005}%2C${
  BUILDING_LON + 0.005
}%2C${BUILDING_LAT + 0.005}`;
const MAP_URL = `https://www.openstreetmap.org/export/embed.html?bbox=${MAP_BBOX}&layer=mapnik&marker=${BUILDING_LAT}%2C${BUILDING_LON}`;
const MAP_LINK = `https://www.openstreetmap.org/?mlat=${BUILDING_LAT}&mlon=${BUILDING_LON}#map=17/${BUILDING_LAT}/${BUILDING_LON}`;

export function About(): JSX.Element {
  usePageMeta();

  return (
    <>
      <PageHero
        eyebrow="About"
        title="About Sunset Harbor"
        lead="Forty-five waterfront condominiums on the Banana River in Cocoa Beach, Florida. Self-managed by the owners' association since 1984."
        photo={{
          src1600: '/photos/image3-1600.webp',
          src800: '/photos/image3-800.webp',
          alt: 'Aerial east-side view of Sunset Harbor Condominiums on the Banana River in Cocoa Beach, showing the full façade with the SUNSET HARBOR rooftop wordmark, the address 3873, and the marina and causeway across the channel.',
          objectPosition: 'center 70%',
        }}
      />

      <SideBySide
        eyebrow="The Building"
        title="Five stories, forty-five homes, one address since 1983"
        body={[
          'Sunset Harbor is a single five-story building with forty-five condominium homes at 3873 South Banana River Boulevard, Cocoa Beach. The original declaration was filed in September 1984; construction had been substantially complete the year before. The building has stood under the same association name for more than four decades.',
          'The floor plate is the same on every level: nine units per floor, numbered 101 through 109 on the first, 201 through 209 on the second, and so on up to 509 on the fifth. Thirty-five of the units are two-bedroom, ten are three-bedroom. Every unit has a balcony or a ground-floor patio, treated as a limited common element.',
        ]}
        facts={[
          { label: 'Built', value: '1983' },
          { label: 'Stories', value: 'Five' },
          { label: 'Units', value: '45 (35 two-bed, 10 three-bed)' },
          { label: 'Construction', value: 'Slab on grade' },
          { label: 'Covered garage spaces', value: '52' },
          { label: 'Florida charter', value: '#N05292' },
        ]}
        photo={{
          src1600: '/photos/image10-1600.webp',
          src800: '/photos/image10-800.webp',
          alt: "Aerial of Sunset Harbor's east elevation with the Banana River filling the background, the mainland visible on the horizon, and palm-lined grounds in the foreground.",
        }}
        bg="white"
        photoSide="right"
      />

      <SideBySide
        eyebrow="The Setting"
        title="On the Banana River Aquatic Preserve"
        body={[
          'The Banana River is not really a river. It is a thirty-one-mile brackish lagoon, part of the Indian River Lagoon system, the most biodiverse estuary in North America. The water along the seawall has been a protected aquatic preserve since 1970: thirty thousand acres set aside for the animals that live and migrate through it.',
          "Manatees, Atlantic bottlenose dolphins, brown pelicans, redfish, tarpon, and snook are routine sightings from the dock. The lagoon is essential manatee habitat. More than a third of Florida's manatees live in or pass through the system, with three to five hundred per day surveyed in the northern lagoon during peak seasons. Owners watch the same wildlife show every morning.",
        ]}
        photo={{
          src1600: '/photos/image1-1600.webp',
          src800: '/photos/image1-800.webp',
          alt: 'Sunset over the Banana River, viewed from Sunset Harbor: warm orange sky, silhouetted palms in the foreground, and the neighboring condominiums to the right.',
        }}
        bg="surface"
        photoSide="left"
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mb-12 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-brand-yellow-deep">
              The Neighborhood
            </p>
            <h2 className="text-3xl font-bold tracking-heading-tight text-brand-charcoal md:text-4xl">
              Two blocks to the Atlantic. Across the street from Ron Jon.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-brand-body-dark">
              Sunset Harbor sits on the Banana River side of a barrier-island town between two great
              bodies of water. The Atlantic Ocean is two blocks east. Ron Jon Surf Shop&apos;s
              corporate headquarters is directly across Banana River Boulevard. Patrick Space Force
              Base is three miles south. The Kennedy Space Center is fifteen miles north. Orlando is
              an hour west.
            </p>
          </div>

          <div className="grid items-stretch gap-8 lg:grid-cols-5 lg:gap-10">
            <div className="lg:col-span-3">
              <div className="overflow-hidden rounded-lg shadow-card">
                <iframe
                  title="Map showing 3873 South Banana River Boulevard, Cocoa Beach, Florida on OpenStreetMap"
                  src={MAP_URL}
                  loading="lazy"
                  className="block aspect-[16/10] w-full border-0"
                />
              </div>
              <p className="mt-2 text-xs text-brand-body-medium">
                <a
                  href={MAP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-brand-yellow-deep/40 underline-offset-2 hover:text-brand-charcoal"
                >
                  View larger map on OpenStreetMap
                </a>
                {' · '}
                <span>Data © OpenStreetMap contributors</span>
              </p>
            </div>

            <ul className="space-y-4 lg:col-span-2">
              {[
                { label: 'Atlantic Ocean', value: '2 blocks east' },
                { label: 'Ron Jon HQ', value: 'Across the street (3850 S Banana River Blvd)' },
                { label: 'Patrick Space Force Base', value: '3 miles south' },
                { label: 'Port Canaveral terminals', value: '10 miles north' },
                { label: 'Kennedy Space Center', value: '15 miles north' },
                { label: 'Orlando International (MCO)', value: '~50 miles west, about an hour' },
              ].map((row) => (
                <li
                  key={row.label}
                  className="flex items-baseline justify-between gap-4 border-b border-brand-surface pb-3"
                >
                  <span className="text-sm font-semibold text-brand-charcoal">{row.label}</span>
                  <span className="text-right text-sm text-brand-body-dark">{row.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>
    </>
  );
}
